import { useEffect, useState } from 'react';
import { Edit, Plus, Power, Search } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Can } from '../../../shared/components/Can';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Pagination } from '../../../shared/components/Pagination';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { FEATURES } from '../../../shared/lib/permissions';
import { ActiveFilter, JobPosition, mensagemDeErro } from '../api';
import { CargoFormModal } from '../components/CargoFormModal';
import { SituacaoBadge } from '../components/SituacaoBadge';
import { useCargos, useSituacaoCargo } from '../hooks';

const DEFAULT_PAGE_SIZE = 25;

export default function CargosPage() {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState<ActiveFilter>('true');
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [offset, setOffset] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<JobPosition | null>(null);
  const [toggling, setToggling] = useState<JobPosition | null>(null);

  // Debounce da busca (300ms), como nas demais listagens.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => setOffset(0), [debounced, active]);

  const cargos = useCargos({ search: debounced || undefined, active, limit, offset });
  const situacao = useSituacaoCargo();
  const items = cargos.data?.data ?? [];
  const count = cargos.data?.pagination.count ?? 0;

  function openForm(cargo: JobPosition | null) {
    setEditing(cargo);
    setFormOpen(true);
  }

  async function confirmToggle() {
    if (!toggling) return;
    try {
      await situacao.mutateAsync({ id: toggling.id, active: !toggling.active });
      toast.success(toggling.active ? `Cargo "${toggling.name}" inativado.` : `Cargo "${toggling.name}" reativado.`);
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível alterar a situação do cargo.'));
    } finally {
      setToggling(null);
    }
  }

  return (
    <PageContainer
      title="Cargos"
      subtitle="Cargos usados na lotação e nas movimentações funcionais."
      breadcrumbs={[{ label: 'Cadastros' }, { label: 'Cargos' }]}
      action={
        <Can feature={FEATURES.masterDataCargos} action="create">
          <Button onClick={() => openForm(null)}>
            <Plus className="mr-2 h-5 w-5" />
            Novo cargo
          </Button>
        </Can>
      }
    >
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-full max-w-md">
          <TextInput
            label="Buscar por código ou nome"
            icon={<Search className="h-5 w-5" />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="min-w-[200px]">
          <AutocompleteSelect
            label="Situação"
            options={[
              { value: 'true', label: 'Ativos' },
              { value: 'false', label: 'Inativos' },
              { value: 'all', label: 'Todos' },
            ]}
            value={active}
            onChange={(v) => setActive(v as ActiveFilter)}
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        {cargos.isLoading ? (
          <p className="p-6 text-sm text-gray-500">Carregando cargos…</p>
        ) : cargos.isError ? (
          <p className="p-6 text-sm text-red-600">{mensagemDeErro(cargos.error, 'Não foi possível carregar os cargos.')}</p>
        ) : items.length === 0 ? (
          <EmptyState title="Nenhum cargo encontrado" description="Ajuste a busca ou cadastre um novo cargo." />
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Código</th>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">CBO</th>
                <th className="px-4 py-3">Situação</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((cargo) => (
                <tr key={cargo.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{cargo.code}</td>
                  <td className="px-4 py-3 text-gray-900">
                    {cargo.name}
                    {cargo.description && <p className="text-xs text-gray-500">{cargo.description}</p>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{cargo.cboCode ?? '—'}</td>
                  <td className="px-4 py-3"><SituacaoBadge active={cargo.active} /></td>
                  <td className="px-4 py-3 text-right">
                    <Can feature={FEATURES.masterDataCargos} action="edit">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => openForm(cargo)}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-blue-600"
                          aria-label={`Editar ${cargo.name}`}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setToggling(cargo)}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-600"
                          aria-label={cargo.active ? `Inativar ${cargo.name}` : `Reativar ${cargo.name}`}
                        >
                          <Power className="h-4 w-4" />
                        </button>
                      </div>
                    </Can>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pagination
        offset={offset}
        limit={limit}
        count={count}
        itemLabel="cargo"
        onChange={(next) => {
          setOffset(next.offset);
          setLimit(next.limit);
        }}
      />

      <CargoFormModal
        open={formOpen}
        cargo={editing}
        onClose={() => setFormOpen(false)}
        onSaved={(message) => {
          setFormOpen(false);
          toast.success(message);
        }}
      />

      <ConfirmDialog
        open={toggling !== null}
        title={toggling?.active ? 'Inativar cargo?' : 'Reativar cargo?'}
        message={
          toggling?.active
            ? `O cargo "${toggling?.name}" deixará de aparecer para novas lotações.`
            : `O cargo "${toggling?.name}" voltará a ficar disponível.`
        }
        confirmLabel={toggling?.active ? 'Inativar' : 'Reativar'}
        danger={toggling?.active}
        busy={situacao.isPending}
        onConfirm={confirmToggle}
        onCancel={() => setToggling(null)}
      />
    </PageContainer>
  );
}
